import SearchBar from '@components/General/SearchBar';
import styles from '@styles/components/Configuracion/Usuarios/ListadoUsuarios.module.css';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import { HollowedButton, NormalButton } from '@components/General/Button';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDebouncedCallback } from '@hooks/useDebouncedCallback';
import Image from '@components/General/Image';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { VirtualizedList } from '@components/General/VirtualizedList';
import { usePopup } from '@contexts/popupContext';
import ConfirmationPopup from '@components/General/ConfirmationPopup';
import { ReactComponent as CloseIcon } from '@assets/SVGs/GeneralUse/XIcon.svg';
import { TextInput } from '@components/General/TextInput';
import ResultPopup from '@components/General/ResultPopup';
import { Application } from '@core/Application';
import { Organization } from '@models/Organization';
import { DropdownListPickerPopper } from '@components/General/TablePoppers';
import { ReactComponent as ArrowsSVG } from '@assets/SVGs/QontactPoint/ArrowSVG.svg';
import { Role } from '@models/Permissions';
import Loading from '@components/General/Loading';

const UsuarioAgregarPopup: React.FC<{orgID: string, doesUserExist: (email: string) => boolean}> = ({orgID, doesUserExist}) => {
    const {showPopup, hidePopup} = usePopup();

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [nameError, setNameError] = useState<string | null>(null);
    const [emailError, setEmailError] = useState<string | null>(null);

    const [loading, setLoading] = useState(false);

    const validateInputs = (): boolean => {
        let valid = true;

        if (name.trim() === "") {
            setNameError("El nombre es obligatorio.".localized());
            valid = false;
        } else {
            setNameError(null);
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setEmailError("El email no es válido.".localized());
            valid = false;
        } else if (doesUserExist(email)) {
            setEmailError("El email ya está en uso.".localized());
            valid = false;
        } else {
            setEmailError(null);
        }

        return valid;
    };

    const handleInviteUser = async () => {

        const service = Application.shared().coreDependencies.usersService;
        setLoading(true);
        try {
            await service.inviteAdminUser(orgID, {name, email: email});
            showPopup(() => <ResultPopup result={"Usuario invitado!".localized()} succesful={true} />);
        } catch (error) {
            console.log(error);
            showPopup(() => <ResultPopup result={"No se pudo invitar al usuario. Inténtalo de nuevo.".localized()} succesful={false} />);
        } finally {
            setLoading(false);
        }
    }


	return (
		<div className={styles['add-user-popup']}>
			<div className={styles['add-user-header']}>
				<h2>Agregar Nuevo Usuario</h2>
				<div className={styles['close-button']} onClick={() => {hidePopup()}}>
					<CloseIcon />
				</div>
			</div>

            <div className={styles['add-user-input']}>
                <TextInput
                    label="Nombre"
                    placeholder={"Ingresa el nombre completo".localized()}
                    onChange={(e) => {setName(e)}}
                    value={name}
                    error={nameError}
                />
            </div>

            <div className={styles['add-user-input']}>
                <TextInput
                    label="Email"
                    placeholder={"Ingresa el email correctamente".localized()}
                    onChange={(e) => {setEmail(e)}}
                    value={email}
                    error={emailError}
                />
            </div>

            <div className={styles['add-user-confirm']}>
                <NormalButton
                    text={"Invitar".localized()}
                    onClick={() => {
                        if (validateInputs()) {
                            handleInviteUser();
                        }
                    }}
                    loading={loading}
                />
            </div>
		</div>
	);
};

const UsuarioEditPopup: React.FC<{ user: User}> = ({ user }) => {
	return <div></div>;
};

const UsuarioRow: React.FC<{
	user: User;
	style: any;
	index: any;
	roleItems: Role[];
	onEditClick: (userID: string) => void;
	onRoleChange?: (userID: string, newRole: string, callback?: any) => void;
	onDisableClick: (userID: string) => void;
}> = ({ user, index, style, onRoleChange, onDisableClick, roleItems }) => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

	const selectedRole = useMemo(() => {
		return roleItems.find(role => role.id === user.role.id);
	}, [user.role]);


	const handleClickOnDiv = (event) => {
        setAnchorElement(event.currentTarget);
        setIsDropdownOpen((previousOpen) => !previousOpen);
    };

	const handleChangeRole = (newRole) => {
		if (newRole.id !== selectedRole.id) {
			if (onRoleChange) {
				onRoleChange(user.id, newRole.name);
			}
		}
		setIsDropdownOpen(false);
	}

	return (
		<div className={styles['users-list-row']} style={{...style, backgroundColor: index % 2 === 0 ? '#FAFAFA' : 'white' }}>
			<div>
				<Image
					src={user.imgURL}
					alt="Profile Picture"
					isRound={true}
					style={{
						width: '35px',
						height: '35px'
					}}
				/>
				<div style={{ display: 'inline-block', verticalAlign: 'top' }}>
					<p className={styles['user-name-p']}>{user.name}</p>
					<p className={styles['user-email-p']}>{user.email}</p>
				</div>
			</div>
			<div>
				{isDropdownOpen && <DropdownListPickerPopper 
					isOpen={isDropdownOpen} 
					anchorElement={anchorElement} 
					placement='bottom' 
					clickAwayHandler={() => setIsDropdownOpen(false)}
					items={roleItems}
					selectedItem={selectedRole}
					setSelectedItem={handleChangeRole}
				/>}

				<div className={styles['role-input-container']} onClick={handleClickOnDiv}>
					<p className={styles['role-dropdown-text']}>{selectedRole && selectedRole.name.localized()}</p>

					<div className={styles['svg-container']} style={{marginLeft: 'auto', marginTop: "2px", width: '10px', height: '10px',
						transform: isDropdownOpen ? 'none' : 'rotate(180deg)',
						transition: 'transform 0.2s ease'
					}}>
						<ArrowsSVG/>
					</div>
				</div>
			</div>
			<div>
				{/* <div
					className={styles['svg-button']}
					onClick={() => {
						onEditClick(user.id);
					}}>
					<BorderColorOutlinedIcon style={{ color: '#EC6C56' }} />
				</div> */}
				<div
					className={styles['svg-button']}
					style={{display: 'flex'}}
					onClick={() => {
						onDisableClick(user.id);
					}}>
					<DeleteOutlineOutlinedIcon style={{ color: '#EC6C56' }} />
				</div>
			</div>
		</div>
	);
};

interface ListadoUsuariosProps {
    org: Organization;
}

type User = {
	id: string;
	name: string;
	email: string;
	role: Role;
	imgURL?: string;
};


const ListadoUsuarios: React.FC<ListadoUsuariosProps> = ({org}) => {
	const { showPopup } = usePopup();

	const [users, setUsers] = useState<User[]>([]);
	const [roles, setRoles] = useState<Role[]>([]);
	const [searchText, setSearchText] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);


    useEffect(() => {
		fetchAdminUsers();
	}, [org]);

	const fetchAdminUsers = async () => {
		setLoading(true);
		const service = Application.shared().coreDependencies.usersService;
		const rolesService = Application.shared().coreDependencies.rolesService;
		try {
			const fetchedUsers = await service.getAdminUsers(org.id);
			const fetchedRoles = await rolesService.getRoles(org.id);
			const rolesLocalized = fetchedRoles.map(role => {
				return {
					...role,
					name: role.name.localized()
				};
			});
			setRoles(rolesLocalized);
			const fetchedAssignments = await rolesService.getAllAssignments(org.id);

			let formattedUsers: User[] = [];
			for (let user of fetchedUsers) {
				const assignment = fetchedAssignments.find(a => a.userId === user.id);
				let role: Role | undefined = undefined;
				if (!assignment) {
					await rolesService.assignRoleToUser(org.id, 'default-admin', user.id);
					console.log(`Assigned default-admin role to user ${user.id}`);
					role = fetchedRoles.find(r => r.id === 'default-admin');
				} else {
					role = fetchedRoles.find(r => r.id === assignment.roleId);
				}
				formattedUsers.push({
					id: user.id,
					name: user.name,
					email: user.email,
					role: role,
					imgURL: user.image,
				});
			}
			setUsers(formattedUsers);
		} catch (error) {
			console.error('Error fetching users:', error);
		} finally {
			setLoading(false);
		}
	};


    const doesUserExist = (email: string): boolean => {
        return users.some((user) => user.email.toLowerCase() === email.toLowerCase());
    }

	const showAddUserPopup = () => {
		showPopup(() => <UsuarioAgregarPopup orgID={org.id} doesUserExist={doesUserExist}/>, false);
	};

	const showDisablePopupForUser = (userId: string) => {
		const user = users.find((u) => u.id === userId);
		if (user) {
			showPopup(() => (
				<ConfirmationPopup
					title={'Deshabilitar Usuario'.localized()}
					message={`¿Estás seguro de que deseas deshabilitar a ${user.name}?`.localized()}
					onConfirm={async () => {
                        const service = Application.shared().coreDependencies.usersService;
                        try {
                            await service.deactivateAdminUser(org.id, userId);
                        } catch (error) {
                            console.error("Error deactivating user:", error);
                            throw error;
                        }
                    }}
					onSuccess={async () => {
						setUsers((prevUsers) => prevUsers.filter((u) => u.id !== userId));
					}}
					successMessage={`Usuario ${user.name} deshabilitado con éxito.`.localized()}
					errorMessage={`No se pudo deshabilitar al usuario ${user.name}. Por favor, inténtalo de nuevo.`.localized()}
				/>
			));
		}
	};

	const showRoleChangeConfirmationPopup = (userId: string, newRole: string, callback?: any) => {
		const user = users.find((u) => u.id === userId);
		if (user) {
			showPopup(() => (
				<ConfirmationPopup
					title={'Cambiar Rol de Usuario'.localized()}
					message={`¿Estás seguro de que deseas cambiar el rol de ${user.name} a ${newRole}?`.localized()}
					onConfirm={async () => {
						const service = Application.shared().coreDependencies.rolesService;
						try {
							const roleToAssign = roles.find((r) => r.name === newRole);
							if (roleToAssign) {
								await service.assignRoleToUser(org.id, roleToAssign.id, userId);
							} else {
								throw new Error("Role not found");
							}
						} catch (error) {
							console.error("Error changing user role:", error);
							throw error;
						}
					}}
					onSuccess={async () => {
						setUsers((prevUsers) =>
							prevUsers.map((u) =>
								u.id === userId ? { ...u, role: roles.find((r) => r.name === newRole) || u.role } : u
							)
						);
						if (callback) {
							callback(roles.find((r) => r.name === newRole));
						}
					}}
					successMessage={`Rol de ${user.name} cambiado a ${newRole} con éxito.`.localized()}
					errorMessage={`No se pudo cambiar el rol de ${user.name}. Por favor, inténtalo de nuevo.`.localized()}
				/>
			));
		}
	};

	const debouncedSetSearch = useDebouncedCallback((value: string) => setSearchText(value), 300);
	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		debouncedSetSearch(e.target.value);
	};

	const filteredUsers = users.filter(
		(user) =>
			user.name.toLowerCase().includes(searchText.toLowerCase()) ||
			user.email.toLowerCase().includes(searchText.toLowerCase()) ||
			user.role.name.toLowerCase().includes(searchText.toLowerCase()),
	);

	const renderRow = useCallback(
		(index: number, style: any) => {
			const user = filteredUsers[index];
			if (!user) return null;
			return (
				<UsuarioRow
					key={user.id}
					user={user}
					index={index}
					style={style}
					roleItems={roles}
					onDisableClick={showDisablePopupForUser}
					onRoleChange={showRoleChangeConfirmationPopup}
					onEditClick={undefined}
				/>
			);
		},
		[filteredUsers, showDisablePopupForUser, showRoleChangeConfirmationPopup],
	);

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<div className={styles['header-title']}>
					<PeopleOutlineOutlinedIcon />
					<p>Listado de Usuarios</p>
				</div>
				<div className={styles['search-bar']}>
					<SearchBar placeholder="Buscar usuario..." onChange={handleSearchChange} />
				</div>
			</div>
			<div className={styles['users-list-container']}>
				<div className={`${styles['users-list-header']}`}>
					<span>Usuario</span>
					<span>Rol</span>
					<span></span>
				</div>

				{loading ? 
					<Loading/>
					:
					<div className={styles['users-list']}>
						<VirtualizedList
							itemCount={filteredUsers.length}
							isItemLoaded={(index) => index < filteredUsers.length}
							renderRow={renderRow}
							itemSize={75}
						/>
					</div>
				}

			</div>
			<div className={styles.footer}>
				<div className={styles['button-container']}>
					<HollowedButton text="+Nuevo Usuario" onClick={showAddUserPopup} />
				</div>
			</div>
		</div>
	);
};

export default ListadoUsuarios;
